import { useCallback, useMemo } from 'react';
import type { DeploymentItem } from '../../components/message-composer/chat-context/selectors';
import type { DashboardProduct } from '../../dashboard/products';

import { instanceOwnName, type PortalInstance } from '../../portal/portalClient';
import { useInstances } from '../useInstances';
import { usePortalInstanceDirectory } from './usePortalInstanceDirectory';

/** The account-stripped name, falling back to the id. */
const deploymentName = (instance: PortalInstance): string => instanceOwnName(instance) || (instance.id as string);

const toItem = (instance: PortalInstance): DeploymentItem => ({
  id: instance.id as string,
  label: deploymentName(instance),
});

/**
 * The instances of the chosen customer(s) — empty until one is chosen, never the full fleet.
 *
 * Distinct from `useInstanceOptions`, which the chat composer keeps: there the whole fleet is in
 * play and search is the portal's job. Here the candidate set is one customer's, small enough for
 * the selector's own filter.
 *
 * Off the portal path it falls back to the guardian id list, unnarrowed: those ids carry no
 * account, so honouring the cascade would leave the picker permanently empty.
 */
export const useDeploymentOptions = ({
  products = [],
  customers = [],
}: {
  products?: DashboardProduct[];
  customers?: string[];
}): {
  items: DeploymentItem[];
  totalCount: number;
  status: 'loading' | 'success' | 'error';
  isServerFiltered: boolean;
  isPortalSource: boolean;
  nameOf: (id: string) => string;
} => {
  const { instances, instancesOfCustomer, nameOf, status, isPortalSource } = usePortalInstanceDirectory(products);

  const {
    data: { instances: guardianInstances },
    status: guardianStatus,
  } = useInstances({ enabled: !isPortalSource });

  const portalItems = useMemo(() => {
    const seen = new Set<string>();
    const items: DeploymentItem[] = [];
    for (const customer of customers) {
      for (const instance of instancesOfCustomer(customer)) {
        if (!instance.id || seen.has(instance.id)) continue;
        seen.add(instance.id);
        items.push(toItem(instance));
      }
    }
    return items;
  }, [customers, instancesOfCustomer]);

  const guardianItems = useMemo(() => guardianInstances.map((id) => ({ id, label: id })), [guardianInstances]);

  // Names a picked deployment as the rows did, off the whole directory: a shared link can
  // name another customer's. The directory's own `nameOf` keeps the account in.
  const dashboardNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const instance of instances) {
      if (instance.id) names.set(instance.id, deploymentName(instance));
    }
    return names;
  }, [instances]);

  const nameOfDeployment = useCallback((id: string) => dashboardNames.get(id) ?? nameOf(id), [dashboardNames, nameOf]);

  return useMemo(() => {
    if (!isPortalSource) {
      return {
        items: guardianItems,
        totalCount: guardianItems.length,
        status: guardianStatus,
        isServerFiltered: false,
        isPortalSource,
        nameOf: nameOfDeployment,
      };
    }
    return {
      items: portalItems,
      totalCount: portalItems.length,
      status,
      isServerFiltered: false,
      isPortalSource,
      nameOf: nameOfDeployment,
    };
  }, [isPortalSource, guardianItems, guardianStatus, portalItems, status, nameOfDeployment]);
};
